const DB_NAME = "fp:image-cache";
const STORE_NAME = "images";
const DB_VERSION = 1;
const MAX_ENTRIES = 240;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const memorySources = new Map();
const inFlight = new Map();
let databasePromise = null;

async function fetchImageWithRenewal(requestUrl) {
  let response = await fetch(requestUrl, { cache: "force-cache", credentials: "omit" });
  if ((response.status === 401 || response.status === 404) && /\/files\/(?:view|preview|stream)\//.test(requestUrl)) {
    const { renewSignedFileUrl } = await import("../utils/fileUrlHelper");
    const renewedUrl = await renewSignedFileUrl(requestUrl);
    if (renewedUrl && renewedUrl !== requestUrl) {
      response = await fetch(renewedUrl, { cache: "force-cache", credentials: "omit" });
    }
  }
  return response;
}

function cacheKeyFor(url, persistentKey = "") {
  const stableKey = String(persistentKey || "").trim();
  if (stableKey) return `stable:${stableKey}`;
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw, typeof window !== "undefined" ? window.location.origin : "https://local.invalid");
    parsed.searchParams.delete("token");
    return parsed.toString();
  } catch {
    return raw.replace(/([?&])token=[^&]+(&?)/i, (_match, prefix, tail) => tail ? prefix : "");
  }
}

function openDatabase() {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (databasePromise) return databasePromise;
  databasePromise = new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "key" });
        store.createIndex("savedAt", "savedAt");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => { databasePromise = null; resolve(null); };
  });
  return databasePromise;
}

async function readEntry(key) {
  const db = await openDatabase();
  if (!db) return null;
  return new Promise((resolve) => {
    const request = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => resolve(null);
  });
}

async function storeEntry(key, blob) {
  const db = await openDatabase();
  if (!db) return;
  await new Promise((resolve) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put({ key, blob, savedAt: Date.now() });
    transaction.oncomplete = resolve;
    transaction.onerror = resolve;
  });
  cleanupOldEntries(db).catch(() => {});
}

async function cleanupOldEntries(db) {
  const entries = await new Promise((resolve) => {
    const request = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => resolve([]);
  });
  const now = Date.now();
  const stale = entries
    .sort((a, b) => Number(b.savedAt || 0) - Number(a.savedAt || 0))
    .filter((entry, index) => index >= MAX_ENTRIES || now - Number(entry.savedAt || 0) > MAX_AGE_MS);
  if (!stale.length) return;
  const transaction = db.transaction(STORE_NAME, "readwrite");
  stale.forEach((entry) => transaction.objectStore(STORE_NAME).delete(entry.key));
}

function objectSource(key, blob) {
  const existing = memorySources.get(key);
  if (existing) return existing;
  const source = URL.createObjectURL(blob);
  memorySources.set(key, source);
  return source;
}

export function peekCachedImageSource(url, persistentKey = "") {
  return memorySources.get(cacheKeyFor(url, persistentKey)) || "";
}

export async function loadPersistentImage(url, persistentKey = "") {
  const requestUrl = String(url || "").trim();
  const key = cacheKeyFor(requestUrl, persistentKey);
  if (!key) return "";
  if (memorySources.has(key)) return memorySources.get(key);
  if (inFlight.has(key)) return inFlight.get(key);

  const promise = (async () => {
    const cached = await readEntry(key);
    if (cached?.blob && Date.now() - Number(cached.savedAt || 0) <= MAX_AGE_MS) {
      return objectSource(key, cached.blob);
    }
    const response = await fetchImageWithRenewal(requestUrl);
    if (!response.ok) throw new Error(`image_${response.status}`);
    const blob = await response.blob();
    if (!String(blob.type || "").startsWith("image/")) throw new Error("invalid_image");
    await storeEntry(key, blob);
    return objectSource(key, blob);
  })();

  inFlight.set(key, promise);
  try { return await promise; }
  finally { inFlight.delete(key); }
}

export async function clearPersistentImageCache() {
  memorySources.forEach((source) => { try { URL.revokeObjectURL(source); } catch {} });
  memorySources.clear();
  inFlight.clear();
  if (typeof indexedDB === "undefined") return;
  const database = await databasePromise;
  try { database?.close?.(); } catch {}
  databasePromise = null;
  await new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = resolve;
    request.onerror = resolve;
    request.onblocked = resolve;
  });
}
